import React from 'react';
import { Loader2, Trash2, MessageSquare, Plus } from 'lucide-react';
import { SessionPanelProps } from './types';

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

interface ExtendedSessionPanelProps extends SessionPanelProps {
  onNewSession?: () => void;
  isCreatingSession?: boolean;
}

export const SessionPanel: React.FC<ExtendedSessionPanelProps> = ({
  sessions,
  activeSessionId,
  isLoading,
  isLoadingSession,
  processingSessionId,
  onSelectSession,
  onDeleteSession,
  onNewSession,
  isCreatingSession,
}) => {
  const handleDelete = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this session?')) {
      onDeleteSession(sessionId);
    }
  };

  return (
    <div className="session-sidebar">
      <div className="session-sidebar-header">
        <h3>Sessions</h3>
        <div className="session-header-buttons">
          {onNewSession && (
            <button
              className="new-session-sidebar-btn"
              onClick={onNewSession}
              disabled={isCreatingSession}
              title={isCreatingSession ? 'Creating session...' : 'New session'}
            >
              {isCreatingSession ? <Loader2 className="spinning" size={16} /> : <Plus size={16} />}
            </button>
          )}
        </div>
      </div>

      <div className="session-list">
        {isLoading ? (
          <div className="loading-sessions">
            <Loader2 className="spinning" size={20} />
            <span>Loading...</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="no-sessions">
            <MessageSquare size={28} className="empty-icon" />
            <p>No sessions yet</p>
            <span className="empty-hint">Start a conversation</span>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.session_id}
              className={`session-item ${session.session_id === activeSessionId ? 'active' : ''}`}
              onClick={() => onSelectSession(session.session_id)}
            >
              <div className="session-info">
                <div className="session-title">
                  {session.title || session.derived_title || 'Untitled Session'}
                </div>
                <div className="session-meta">
                  <span>{formatDate(session.updated_at || session.created_at)}</span>
                  {typeof session.turn_count === 'number' && (
                    <span className="message-count">
                      {session.turn_count} turn{session.turn_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  {(processingSessionId === session.session_id || session.last_turn_status === 'processing') && (
                    <span className="message-count session-processing">Running</span>
                  )}
                  {isLoadingSession && session.session_id === activeSessionId && (
                    <span className="message-count session-processing">Syncing</span>
                  )}
                </div>
              </div>
              <button
                className="delete-session-btn"
                onClick={(e) => handleDelete(e, session.session_id)}
                title="Delete session"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SessionPanel;
